import React from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  MonetizationOn,
  AccountBalance,
  Receipt,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { Transaction } from '../../types';
import { StockNameManager } from '../../services/stockNameManager';
import {
  getTrendColorSet,
  getTrendTextColor,
} from '../../theme/taiwanTrendColors';

interface RecentTransactionsProps {
  transactions: Transaction[];
}

const RecentTransactions: React.FC<RecentTransactionsProps> = ({ transactions }) => {
  // Get recent transactions (latest 5)
  const recentTransactions = transactions.slice(0, 5);

  // Get transaction type info
  const getTransactionTypeInfo = (type: string) => {
    switch (type) {
      case 'buy':
        return { label: '買入', icon: <TrendingUp sx={{ fontSize: 16, color: 'inherit' }} />, direction: 'down' as const };
      case 'sell':
        return { label: '賣出', icon: <TrendingDown sx={{ fontSize: 16, color: 'inherit' }} />, direction: 'up' as const };
      case 'dividend':
        return { label: '股利', icon: <MonetizationOn sx={{ fontSize: 16, color: 'inherit' }} />, direction: 'up' as const };
      case 'cash_deposit':
        return { label: '存入現金', icon: <AccountBalance sx={{ fontSize: 16, color: 'inherit' }} />, direction: 'up' as const };
      case 'cash_withdrawal':
        return { label: '提取現金', icon: <AccountBalance sx={{ fontSize: 16, color: 'inherit' }} />, direction: 'down' as const };
      case 'stock_increase':
        return { label: '股票增加', icon: <TrendingUp sx={{ fontSize: 16, color: 'inherit' }} />, direction: 'up' as const };
      case 'stock_decrease':
        return { label: '股票減少', icon: <TrendingDown sx={{ fontSize: 16, color: 'inherit' }} />, direction: 'down' as const };
      default:
        return { label: type, icon: <Receipt sx={{ fontSize: 16, color: 'inherit' }} />, direction: 'neutral' as const };
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" fontWeight="bold">
          📊 最近交易
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={() => (window.location.href = "/#/transactions")}
        >
          查看全部
        </Button>
      </Box>
      
      {recentTransactions.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Receipt sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            暫無交易記錄
          </Typography>
          <Typography variant="body2" color="text.secondary">
            開始您的投資旅程吧！
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {recentTransactions.map((transaction) => {
            const typeInfo = getTransactionTypeInfo(transaction.type);
            const trendValue = typeInfo.direction === 'up' ? 1 : typeInfo.direction === 'down' ? -1 : 0;
            const chipColors = getTrendColorSet(trendValue);
            const chipTextColor = trendValue === 0 ? 'text.primary' : '#fff';
            const amountColor = getTrendTextColor(trendValue);

            return (
              <Card
                key={transaction.id}
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    transform: 'translateY(-2px)',
                  }
                }}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  {/* 第一行：股票信息和交易類型 */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box>
                        <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem', lineHeight: 1.2 }}>
                          {transaction.symbol}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                          {StockNameManager.getStockName(transaction.symbol)}
                        </Typography>
                      </Box>
                    </Box>

                    <Chip
                      icon={typeInfo.icon}
                      label={typeInfo.label}
                      variant={trendValue === 0 ? 'outlined' : 'filled'}
                      size="medium"
                      sx={{
                        fontWeight: 'bold',
                        fontSize: '0.85rem',
                        color: chipTextColor,
                        backgroundColor: trendValue === 0 ? chipColors.light : chipColors.primary,
                        borderColor: chipColors.border,
                        '& .MuiChip-icon': {
                          fontSize: '1rem',
                          color: chipTextColor
                        }
                      }}
                    />
                  </Box>

                  {/* 第二行：交易詳情 - 完整寬度分佈 */}
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: '1fr 1fr 1fr 1fr', 
                    gap: 1.5, 
                    mb: 1,
                    textAlign: 'center'
                  }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', mb: 0.5 }}>
                        股數
                      </Typography>
                      <Typography variant="body1" fontWeight="bold" sx={{ fontSize: '0.95rem' }}>
                        {transaction.quantity.toLocaleString()}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', mb: 0.5 }}>
                        價格
                      </Typography>
                      <Typography variant="body1" fontWeight="bold" sx={{ fontSize: '0.95rem' }}>
                        ${transaction.price}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', mb: 0.5 }}>
                        {
                          ['stock_increase', 'stock_decrease'].includes(transaction.type) 
                            ? '影響' 
                            : '總金額'
                        }
                      </Typography>
                      <Typography 
                        variant="body1" 
                        fontWeight="bold" 
                        sx={{ 
                          fontSize: '0.95rem',
                          color: ['stock_increase', 'stock_decrease'].includes(transaction.type)
                            ? 'text.primary'
                            : amountColor
                        }}
                      >
                        {
                          ['stock_increase', 'stock_decrease'].includes(transaction.type)
                            ? '無變動'
                            : `${transaction.type === 'buy' ? '-' : '+'}$${Math.round(transaction.total).toLocaleString()}`
                        }
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', mb: 0.5 }}>
                        時間
                      </Typography>
                      <Typography variant="body1" fontWeight="medium" sx={{ fontSize: '0.9rem' }}>
                        {(() => {
                          try {
                            const dateToFormat = transaction.transactionDate || transaction.timestamp;
                            if (!dateToFormat) return 'N/A';
                            return format(dateToFormat, "MM/dd HH:mm");
                          } catch (error) {
                            console.warn('Date formatting error:', error);
                            return 'Invalid Date';
                          }
                        })()
                      }
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}
    </Paper>
  );
};

export default RecentTransactions;
